import type { AppFunnelStep, AppFunnelStepIndexed } from '~/types/commons'
import type { RouteLocationNamedRaw } from '#vue-router'

interface AppFunnelConfig {
  scrollTopOnStepChange: boolean
}

export const useAppFunnel = <T>({ steps, config }: { steps: MaybeRef<AppFunnelStep<T>[]>, config?: AppFunnelConfig }) => {
  const route = useRoute()
  const availableSteps = computed(() => {
    return toValue(steps)
      .filter(({ isAvailable }) => isAvailable)
      .reduce<AppFunnelStepIndexed<T>[]>((acc, curr, index) => {
        const parent = { ...curr, index }
        acc.push(parent)
        if (Array.isArray(curr.subSteps)) {
          const subSteps = curr.subSteps.map(subStep => ({ ...subStep, index, parent }))
          acc.push(...subSteps)
          parent.subSteps = subSteps // Just making sure we have the correct object reference there between the objects in the allSteps array
        }
        return acc
      }, [])
  })

  const currentStepIndex = computed(() => {
    const index = availableSteps.value.findIndex(({ routeRawName }) => routeRawName === route.name)
    return index
  })

  const currentStep = computed<AppFunnelStepIndexed<T> | undefined>(() => {
    if (currentStepIndex.value === -1) {
      return undefined
    }
    return availableSteps.value[currentStepIndex.value]
  })

  const currentStepTitle = computed(() => currentStep.value?.title ?? currentStep.value?.parent?.title)

  const previousStep = computed(() => currentStepIndex.value >= 1 ? availableSteps.value[currentStepIndex.value - 1] : null)

  const nextStep = computed(() => currentStepIndex.value < availableSteps.value.length ? availableSteps.value[currentStepIndex.value + 1] : null)

  const indexedStepsCount = computed(() => availableSteps.value.reduce((sum, curr) => curr.unindexed ? sum : sum + 1, 0))

  const isFirstStep = computed(() => {
    const currentStepValue = toValue(currentStep)
    if (!isDefined(currentStepValue)) {
      return false
    }
    return currentStepValue.id === availableSteps.value[0].id
  })

  const isLastStep = computed(() => {
    const currentStepValue = toValue(currentStep)
    if (!isDefined(currentStepValue) || availableSteps.value.length < 1) {
      return false
    }
    return currentStepValue.id === availableSteps.value[availableSteps.value.length - 1].id
  })

  const onStepSuccess = async ({ params, query }: { params?: RouteLocationNamedRaw['params'], query?: RouteLocationNamedRaw['query'] } = {}) => {
    if (config?.scrollTopOnStepChange) {
      scrollToTop()
    }

    if (isDefined(nextStep.value)) {
      await navigateTo({
        name: nextStep.value.routeRawName,
        params: { ...route.params, ...params },
        query: { ...route.query, ...query },
      })
      return
    }

    // happen when isAvailable properity is updated just before firing this method
    // ie: a booking switching from `abandonned` to `validated`
    if (isDefined(currentStep.value) && currentStep.value.routeRawName !== route.name) {
      await navigateTo({
        name: currentStep.value.routeRawName,
        params: { ...route.params, ...params },
        query: { ...route.query, ...query },
      })
      return
    }
  }

  const onStepCancel = async () => {
    if (config?.scrollTopOnStepChange) {
      scrollToTop()
    }
    if (previousStep.value) {
      await navigateTo({
        name: previousStep.value.routeRawName,
        params: route.params,
        query: route.query,
      })
    }
  }

  const loading = ref<boolean>(false)

  return {
    availableSteps,
    loading,
    currentStep,
    currentStepIndex,
    currentStepTitle,
    previousStep,
    nextStep,
    indexedStepsCount,
    isFirstStep,
    isLastStep,
    onStepSuccess,
    onStepCancel,
  }
}
